import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Crown, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";

const FEATURES = [
  "Daily Insights from a dermatologist + trichologist",
  "Adaptive weekly routines that evolve with you",
  "Dupe Finder for any beauty product",
  "Premium DIY recipes locked just for you",
  "Glow Challenges + collectible badges",
  "Progress trends, Glow Score & analytics",
];

export default function UpgradeModal({ open, onClose, feature }) {
  const navigate = useNavigate();
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-cocoa/40 backdrop-blur-sm"
            style={{ zIndex: 100002 }}
            onClick={onClose}
            data-testid="upgrade-modal-backdrop"
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 220 }}
            className="fixed bottom-0 inset-x-0 mx-auto max-w-[430px] w-full bg-white rounded-t-[28px] flex flex-col"
            style={{ maxHeight: "85vh", zIndex: 100003 }}
            data-testid="upgrade-modal"
          >
            {/* Sticky header — handle + close button always visible */}
            <div className="shrink-0 relative border-b border-transparent">
              <div className="flex justify-center pt-3 pb-1">
                <div className="w-12 h-1.5 rounded-full bg-cocoa/15" />
              </div>
              <button
                onClick={onClose}
                data-testid="upgrade-modal-close"
                aria-label="Close"
                className="absolute top-3 right-4 w-9 h-9 rounded-full bg-cream flex items-center justify-center"
              >
                <X size={18} className="text-cocoa/60" />
              </button>
            </div>

            {/* Scrollable body — content can never overflow the 85vh cap */}
            <div className="flex-1 overflow-y-auto overscroll-contain px-6 pb-8 pt-2">
              <div className="mb-4">
                <div
                  className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3"
                  style={{ background: "linear-gradient(135deg, #C9975A 0%, #E3BA85 100%)" }}
                >
                  <Crown size={14} className="text-white" />
                  <span className="text-xs uppercase tracking-[0.18em] font-medium text-white">
                    Glow Premium
                  </span>
                </div>
                <h2 className="text-3xl text-cocoa font-serif leading-tight">
                  Unlock {feature || "Premium"}
                </h2>
                <p className="text-sm text-cocoa/60 mt-1 font-light">
                  Your complete personalized beauty operating system.
                </p>
              </div>

              <ul className="space-y-2.5 mb-6">
                {FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm font-light text-cocoa/85">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-rose/10 flex items-center justify-center shrink-0">
                      <Check size={12} className="text-rose" strokeWidth={2.5} />
                    </span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <div className="flex gap-3">
                <button
                  data-testid="upgrade-monthly-btn"
                  onClick={() => {
                    onClose?.();
                    navigate("/app/premium");
                  }}
                  className="flex-1 rounded-full border border-cocoa/15 py-3.5 font-medium text-sm text-cocoa hover:bg-cream transition-all"
                >
                  $12/month
                </button>
                <button
                  data-testid="upgrade-yearly-btn"
                  onClick={() => {
                    onClose?.();
                    navigate("/app/premium");
                  }}
                  className="flex-1 rounded-full py-3.5 font-medium text-sm text-white relative overflow-hidden"
                  style={{ background: "linear-gradient(135deg, #C9975A 0%, #E3BA85 100%)" }}
                >
                  $89/yr · save 38%
                </button>
              </div>
              <p className="text-[11px] text-cocoa/45 text-center mt-3 font-light">
                Yearly ≈ $7.42/month. Cancel anytime.
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
